<!-- Saved video dialog (#savedVideoModal, data in data.savedVideos): new or existing saved video. Fields in order: link
     (required; the platform is recognised from it and shown next to it), title (may stay empty: "<platform> video" is
     shown), project (with at least two projects; move, or copy to another project, for a saved video), tags of the
     Saved videos group (TagPicker.vue, separate from the plan's formats, "+" creates one; a new video starts with the
     page's tag filter), thumbnail (click,
     drop or image URL; ImageBox.vue; YouTube links have one already), notes with @mentions. A link dropped on the Saved
     videos page fills the link (prefill, src/stores/drop.ts). Delete asks first. Opened through the dialogs store
     (create button, Saved videos page, search, drop); logic in src/features/savedVideos.ts. Like the source dialog, it
     closes only with ‹, × or the buttons; Enter in link or title, Ctrl/Cmd+S and Ctrl/Cmd+Enter save. From 1024 px: link and
     thumbnail on the left, title, project, category and tags on the right. -->
<script setup lang="ts">
import EditDialogFooter from '../fields/EditDialogFooter.vue'
import { INPUT_CLS, requiredCls } from '../../utils/fieldCls'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import AppDialog from '../AppDialog.vue'
import ImageBox from '../fields/ImageBox.vue'
import ProjectPicker from '../fields/ProjectPicker.vue'
import MentionField from '../fields/MentionField.vue'
import TagPicker from '../fields/TagPicker.vue'
import KindPicker from '../fields/KindPicker.vue'
import RefUsage from '../video/RefUsage.vue'
import { useI18n } from '../../composables/useI18n'
import { useDataStore } from '../../stores/data'
import { deleteSaved, platformInfo, saveSaved, savedForm, savedPlatform } from '../../features/savedVideos'
import { validProject } from '../../features/projects'
import { readableHex } from '../../features/theme'
import { newItemProject } from '../../stores/projects'
import { confirmDelete, uiAlertThen } from '../../stores/notices'
import { enterKey } from '../../features/dialogs'
import { finePointer } from '../../utils/media'
import { saveAppData } from '../../features/storage/save'

const props = defineProps<{ id: string | null; prefill?: { url?: string }; tag?: string; kind?: string }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const store = useDataStore()

const form = reactive(savedForm(store.getData(), props.id, newItemProject(), props.tag, props.kind))
const tagPicker = ref<InstanceType<typeof TagPicker> | null>(null)
if (props.prefill?.url) form.url = props.prefill.url

const platform = computed(() => (form.url.trim() ? platformInfo(savedPlatform({ url: form.url })) : null))

const urlInput = ref<HTMLInputElement | null>(null)
const titleInput = ref<HTMLInputElement | null>(null)
// A new video starts from the link; a dropped link is there already: the title is next (with a mouse: no phone keyboard
// over the dialog)
onMounted(() => { if (!props.id && finePointer()) nextTick(() => (props.prefill?.url ? titleInput.value : urlInput.value)?.focus()) })
// The link is required: shown in the main colour, with the cursor in it, after the message
const missing = ref(false)
watch(() => form.url, v => { if (v.trim()) missing.value = false })
const onEnter = enterKey(save)

function save() {
  if (!form.url.trim()) { missing.value = true; void uiAlertThen(t('Paste the link of the video!')).then(() => urlInput.value?.focus()); return }
  const tags = tagPicker.value ? tagPicker.value.flush() : form.tags
  const d = store.getData()
  if (!saveSaved(d, props.id, { ...form, tags, projectId: validProject(d, form.projectId, newItemProject()) })) return
  saveAppData(d)
  emit('close')
}

async function remove() {
  if (!props.id) return
  if (!(await confirmDelete(t('Are you sure you want to delete this saved video?')))) return
  const d = store.getData()
  deleteSaved(d, props.id)
  saveAppData(d)
  emit('close')
}
</script>

<template>
  <AppDialog id="savedVideoModal" name="saved" title-id="savedModalTitle" :title="id ? t('Edit saved video') : t('Save a video')" editing :save="save" @close="emit('close')">
    <div class="space-y-5 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-x-6 lg:gap-y-5 lg:items-start">
    <div class="lg:col-start-1">
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('VIDEO LINK') }}</label>
      <div class="relative">
        <input id="inputSavedUrl" ref="urlInput" v-model="form.url" type="url" inputmode="url" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="off" placeholder="https://www.instagram.com/reel/…"
          :class="[requiredCls(missing), { 'pr-32': platform }]" @keydown.enter="onEnter">
        <span v-if="platform" class="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 text-xs font-bold text-gray-300 pointer-events-none">
          <i :class="platform.icon" :style="platform.color ? { color: readableHex(platform.color) } : undefined"></i>{{ platform.name }}</span>
      </div>
    </div>

    <div class="lg:col-start-2">
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider"><span>{{ t('TITLE') }}</span> <span class="font-normal text-gray-500 tracking-normal">{{ t('(not required)') }}</span></label>
      <input id="inputSavedTitle" ref="titleInput" v-model="form.title" type="text" :placeholder="t('E.g. Great hook in the first 3 seconds')" @keydown.enter="onEnter"
        :class="INPUT_CLS">
    </div>

    <ProjectPicker v-model="form.projectId" kind="saved" :item-id="id" class="lg:col-start-2" />

    <div class="lg:col-start-2">
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('CATEGORY') }} <span class="font-normal text-gray-500 tracking-normal">{{ t('(what kind of video it is)') }}</span></label>
      <KindPicker id="savedKindPicker" v-model="form.kind" group="saved" />
    </div>

    <div class="lg:col-start-2">
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('TAGS') }}</label>
      <TagPicker id="savedTagPicker" ref="tagPicker" v-model="form.tags" kind="saved" :project-id="form.projectId" />
    </div>

    <div class="lg:col-start-1 lg:row-start-2 lg:row-span-4">
      <ImageBox v-model="form.thumbnail" id="savedThumbBox" file-id="savedThumbFile" :placeholder="t('Change thumbnail')" url-field url-id="inputSavedThumbUrl" />
    </div>
    </div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('NOTES') }}</label>
      <MentionField id="inputSavedNote" v-model="form.note" :placeholder="t('Why you saved it, what to copy… (@ to mention)')"
        class="min-h-[5rem] w-full bg-darkinput border border-gray-800 rounded-lg p-4 text-gray-100 focus:outline-none focus:border-brand transition" />
    </div>

    <RefUsage group="saved" :id="id" @open="emit('close')" />

    <template #footer>
      <EditDialogFooter delete-id="deleteSavedBtn" :can-delete="!!id" :delete-label="t('Delete saved video')" @delete="remove" @save="save" />
    </template>
  </AppDialog>
</template>
